import dayjs from 'dayjs';
import { useMemo } from 'react';

import { calculateStartAndEndTimeFromTimeModel } from '@/Pages/SingleDashboard/hooks/useChartTimeFilter';
import { useCurrentOrganizationBilling, useOrgUsage } from '@/hooks/useSwr';
import { lowerCaseIncludes } from '@/utils/stringUtils';

export const useTraceCount = () => {
  const {
    data: currentOrganizationBilling,
    isLoading: isLoadingCurrentOrganizationBilling,
  } = useCurrentOrganizationBilling();
  const { end_of_billing_period: endOfBillingPeriod } =
    currentOrganizationBilling ?? {};
  const { start_time: startTimestamp, end_time: endTimestamp } = useMemo(
    () =>
      endOfBillingPeriod
        ? calculateStartAndEndTimeFromTimeModel({
            end_time: endOfBillingPeriod,
            start_time: dayjs(endOfBillingPeriod)
              .utc()
              .subtract(1, 'month')
              .toISOString(),
          })
        : {
            start_time: dayjs.utc().subtract(1, 'month').toISOString(),
            end_time: dayjs.utc().toISOString(),
          },
    [endOfBillingPeriod]
  );

  const { data: orgUsage, isLoading } = useOrgUsage(
    {
      starting_on: startTimestamp,
      ending_before: endTimestamp,
    },
    { revalidateOnFocus: false }
  );

  // look for billable metric name that includes 'LangSmith Traces' and only base traces
  // note: if this changes in metronome, we need to update this
  const traceUsage = orgUsage?.filter(
    ({ billable_metric_name: metricName }) =>
      lowerCaseIncludes(metricName, 'langsmith traces') &&
      lowerCaseIncludes(metricName, 'base')
  );

  const total =
    traceUsage?.reduce((acc, usage) => {
      if (usage.value) {
        return acc + usage.value;
      }
      if (usage.groups) {
        return (
          acc +
          Object.values(usage.groups).reduce((acc, value) => acc + value, 0)
        );
      }
      return acc;
    }, 0) || 0;

  return { total, isLoading: isLoadingCurrentOrganizationBilling || isLoading };
};
